import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AdminService } from "../api/AdminService";
import ConfirmDestructiveModal from "../components/ConfirmDestructiveModal";
import { useJobProgress } from "../hooks/useJobProgress";

const ROOTS = [
  { key: "app_data", label: "App Data" },
  { key: "user_data", label: "User Data" },
];

const PAGE_SIZE = 200;
const SEARCH_DEBOUNCE_MS = 400;

const formatSize = (bytes) => {
  if (bytes === null || bytes === undefined) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const AdminFilesPage = () => {
  const [root, setRoot] = useState("app_data");
  const [path, setPath] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { name, isDir }
  const [deleting, setDeleting] = useState(false);
  const [zippingKey, setZippingKey] = useState(null); // "." for current dir, or an entry name
  const [zipProgress, setZipProgress] = useState(null); // { pct, message }
  const [uploadingZip, setUploadingZip] = useState(false);
  const [uploadZipProgress, setUploadZipProgress] = useState(null); // { pct, message }
  const fileInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const sentinelRef = useRef(null);
  const { subscribeToJob } = useJobProgress();

  const loadPage = async (offset, { append } = {}) => {
    (append ? setLoadingMore : setLoading)(true);
    try {
      const data = await AdminService.listEfsDir(root, path, { search, limit: PAGE_SIZE, offset });
      setEntries((prev) => (append ? [...prev, ...data.entries] : data.entries));
      setTotal(data.total);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to list directory");
      if (!append) setEntries([]);
    } finally {
      (append ? setLoadingMore : setLoading)(false);
    }
  };

  // Reset and reload from scratch whenever root/path/search changes.
  useEffect(() => {
    loadPage(0, { append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root, path, search]);

  // Debounce the search box into the server-side `search` query param.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || entries.length >= total) return;
    loadPage(entries.length, { append: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadingMore, entries.length, total, root, path, search]);

  // Auto-load the next page as the sentinel row scrolls into view, instead
  // of a manual "load more" click — this directory can have 90k+ entries.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (observerEntries) => {
        if (observerEntries[0].isIntersecting) loadMore();
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore]);

  const switchRoot = (nextRoot) => {
    setRoot(nextRoot);
    setPath("");
    setSearchInput("");
    setSearch("");
  };

  const openDir = (name) => {
    setPath(path ? `${path}/${name}` : name);
    setSearchInput("");
    setSearch("");
  };

  const breadcrumbs = () => {
    const segments = path ? path.split("/") : [];
    return [
      { label: ROOTS.find((r) => r.key === root).label, path: "" },
      ...segments.map((seg, idx) => ({
        label: seg,
        path: segments.slice(0, idx + 1).join("/"),
      })),
    ];
  };

  const handleDownload = async (name) => {
    try {
      const filePath = path ? `${path}/${name}` : name;
      const blob = await AdminService.downloadEfsFile(root, filePath);
      triggerBlobDownload(blob, name);
    } catch (error) {
      toast.error("Failed to download file");
    }
  };

  const handleUpload = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      const result = await AdminService.uploadEfsFiles(root, path, fileList);
      if (result.uploaded?.length) {
        toast.success(`Uploaded ${result.uploaded.length} file(s)`);
      }
      if (result.failed?.length) {
        toast.error(`Failed: ${result.failed.map((f) => f.filename).join(", ")}`);
      }
      loadPage(0, { append: false });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        Array.isArray(detail)
          ? detail.map((f) => f.filename).join(", ") + " failed to upload"
          : detail || "Failed to upload files",
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Upload-side counterpart to handleDownloadZip: pushes a whole folder
  // (with subfolders) back in one request instead of requiring the admin
  // to recreate the structure by hand and upload into each subfolder.
  const handleUploadZip = async (e) => {
    const zipFile = e.target.files?.[0];
    if (!zipFile) return;
    setUploadingZip(true);
    setUploadZipProgress({ pct: 0, message: "Uploading archive..." });
    try {
      const { job_id: jobId } = await AdminService.startEfsZipUpload(root, path, zipFile);
      await subscribeToJob(jobId, {
        onUpdate: (job) => setUploadZipProgress({ pct: job.progress_pct, message: job.message }),
        onComplete: (result) => {
          toast.success(result?.extracted ? `Extracted ${result.extracted} file(s)` : "Extraction complete");
          if (result?.skipped?.length) {
            toast.warning(`Skipped ${result.skipped.length} file(s) — unsafe path or over the size limit`);
          }
          setUploadingZip(false);
          setUploadZipProgress(null);
          loadPage(0, { append: false });
        },
        onError: (message) => {
          toast.error(message || "Failed to extract archive");
          setUploadingZip(false);
          setUploadZipProgress(null);
        },
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to upload archive");
      setUploadingZip(false);
      setUploadZipProgress(null);
    } finally {
      if (zipInputRef.current) zipInputRef.current.value = "";
    }
  };

  // Zipping runs as a background job on the backend (some folders here hold
  // tens of thousands of files) — kick it off, show live percent via the
  // shared job_progress WS channel, then fetch the finished archive.
  const handleDownloadZip = async (key, dirPath, downloadName) => {
    setZippingKey(key);
    setZipProgress({ pct: 0, message: "Starting..." });
    try {
      const { job_id: jobId } = await AdminService.startEfsDirZip(root, dirPath);
      await subscribeToJob(jobId, {
        onUpdate: (job) => setZipProgress({ pct: job.progress_pct, message: job.message }),
        onComplete: async () => {
          try {
            const blob = await AdminService.downloadZipResult(root, jobId);
            triggerBlobDownload(blob, `${downloadName}.zip`);
          } catch (error) {
            toast.error("Zip finished but the download failed — try again");
          } finally {
            setZippingKey(null);
            setZipProgress(null);
          }
        },
        onError: (message) => {
          toast.error(message || "Failed to zip folder");
          setZippingKey(null);
          setZipProgress(null);
        },
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to start zip job");
      setZippingKey(null);
      setZipProgress(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const targetPath = path ? `${path}/${deleteTarget.name}` : deleteTarget.name;
      await AdminService.deleteEfsPath(root, targetPath, deleteTarget.isDir);
      toast.success(`Deleted ${deleteTarget.name}`);
      setDeleteTarget(null);
      loadPage(0, { append: false });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold">EFS File Manager</h2>
        <div className="flex gap-2">
          <input
            type="file"
            multiple
            ref={fileInputRef}
            className="hidden"
            onChange={handleUpload}
          />
          <button
            className="btn btn-primary btn-sm"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? "Uploading..." : "Upload here"}
          </button>
          <input
            type="file"
            accept=".zip"
            ref={zipInputRef}
            className="hidden"
            onChange={handleUploadZip}
          />
          <button
            className="btn btn-outline btn-sm"
            disabled={uploadingZip}
            onClick={() => zipInputRef.current?.click()}
            title="For a whole folder with subfolders: zip it locally, then upload the archive here to extract"
          >
            {uploadingZip
              ? `Extracting... ${uploadZipProgress?.pct ?? 0}%`
              : "Upload zip (extract here)"}
          </button>
        </div>
      </div>

      <div className="tabs tabs-boxed mb-3 w-fit">
        {ROOTS.map((r) => (
          <button
            key={r.key}
            className={`tab ${root === r.key ? "tab-active" : ""}`}
            onClick={() => switchRoot(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="breadcrumbs text-sm">
          <ul>
            {breadcrumbs().map((crumb) => (
              <li key={crumb.path}>
                <button className="link" onClick={() => setPath(crumb.path)}>
                  {crumb.label || "/"}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search this folder..."
            className="input input-bordered input-sm w-56"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button
            className="btn btn-outline btn-xs"
            disabled={zippingKey === "."}
            onClick={() =>
              handleDownloadZip(".", path, path ? path.split("/").pop() : root)
            }
          >
            {zippingKey === "."
              ? `Zipping... ${zipProgress?.pct ?? 0}%`
              : "Download this folder as .zip"}
          </button>
        </div>
      </div>

      <p className="mb-2 text-xs text-base-content/60">
        {total.toLocaleString()} item{total === 1 ? "" : "s"}
        {search && ` matching "${search}"`} — showing {entries.length.toLocaleString()}
      </p>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Name</th>
                <th>Size</th>
                <th>Modified</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.name}>
                  <td>
                    {entry.is_dir ? (
                      <button
                        className="link font-semibold"
                        onClick={() => openDir(entry.name)}
                      >
                        {entry.name}/
                      </button>
                    ) : (
                      entry.name
                    )}
                  </td>
                  <td>{entry.is_dir ? "-" : formatSize(entry.size)}</td>
                  <td>
                    {entry.modified_at
                      ? new Date(entry.modified_at).toLocaleString()
                      : "-"}
                  </td>
                  <td className="flex gap-1">
                    {!entry.is_dir && (
                      <button
                        className="btn btn-outline btn-xs"
                        onClick={() => handleDownload(entry.name)}
                      >
                        Download
                      </button>
                    )}
                    {entry.is_dir && (
                      <button
                        className="btn btn-outline btn-xs"
                        disabled={zippingKey === entry.name}
                        onClick={() =>
                          handleDownloadZip(
                            entry.name,
                            path ? `${path}/${entry.name}` : entry.name,
                            entry.name,
                          )
                        }
                      >
                        {zippingKey === entry.name
                          ? `Zipping... ${zipProgress?.pct ?? 0}%`
                          : "Download as .zip"}
                      </button>
                    )}
                    <button
                      className="btn btn-error btn-outline btn-xs"
                      onClick={() =>
                        setDeleteTarget({ name: entry.name, isDir: entry.is_dir })
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-base-content/60">
                    {search ? "No matches" : "Empty directory"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {entries.length < total && (
            <div ref={sentinelRef} className="py-4 text-center text-sm text-base-content/60">
              {loadingMore ? "Loading more..." : ""}
            </div>
          )}
        </div>
      )}

      <ConfirmDestructiveModal
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.isDir ? "directory" : "file"}`}
        description={
          deleteTarget?.isDir
            ? "This permanently deletes this directory and everything inside it from EFS."
            : "This permanently deletes this file from EFS."
        }
        confirmText={deleteTarget?.name || ""}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AdminFilesPage;
